<script>
    /**
     * SPDX-FileCopyrightText: 2012-2026 Open Assessment Technologies S.A.
     * Copyright (C) 2026 (original work) Open Assessment Technologies S.A.
     *
     * SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-TAO-Commercial-License
     */
    import { onMount, onDestroy } from 'svelte';
    import { __, generateElementId } from '@oat-sa-private/ui-core';
    import { Feedback } from '@oat-sa-private/ui-elements';
    import { Panel, NotificationContainer, clearNotifications } from '@oat-sa-private/ui-components';
    import createVisibilityObserver from './layout/visibilityObserver.svelte';
    import HeaderBar from './layout/HeaderBar.svelte';
    import Transition from './layout/Transition.svelte';
    import ItemHanger from './layout/itemHanger/ItemHanger.svelte';
    import UserMenu from './layout/userMenu/UserMenu.svelte';
    import { testSessionStatus } from './session/sessionStates.js';
    import { isItemModalFeedbackState } from './util/testContext.js';
    import { getTestSessionStatusStore, getTestStateStore } from './testsStateStore.js';
    import { setScreenSize, screenSize } from './screenSizeStore.js';
    import { getTestSessionUserDataService } from './session/testSessionUserDataService.js';
    import { getNavigationFeedbacksStore } from './feedback';
    import Overlay from './layout/overlay/Overlay.svelte';
    import FloatingToolbarAreas from './layout/FloatingToolbarAreas.svelte';
    import { createToolbarItemsApi } from './layout/toolbarItems.js';
    import resizeObserve from '@oat-sa-private/tao-item-runner-qtinui/src/runner/interactions/util/actions/resizeObserve.js';
    import FeedbackDialogsContainer from './feedback/FeedbackDialogsContainer.svelte';
    import { testLayoutStore, clearTestLayoutStore } from './layout/testLayoutStore.js';

    /**
     * The test runner main layout component
     */

    /**
     * @typedef {Object} Props
     * @property {string} serviceCallId - id of test session
     * @property {Object} [theme] - contains the test runner theming information
     * @property {Object} [theme.logo] - a custom logo
     * @property {string} [theme.logo.src] - custom logo source
     * @property {string} [theme.logo.alt] - custom logo alternative text
     * @property {boolean} [theme.hideMenuButton] - hide menu button from header
     * @property {boolean} [theme.showUserMenu] - hide user menu from header
     * @property {Object?} [testTaker] - user info
     * @property {Object|null} [inlineNotification] - notification rendered above the item content
     * @property {Object[]} [itemHangerMessages] - list of item hanger messages
     * @property {Object} [plugins] - plugins object, in format { pluginName: PluginAPI }
     * @property {Function} [onMounted]
     * @property {Function} [onToolbarAction]
     */
    /** @type {Props} */
    let {
        serviceCallId,
        theme = {},
        testTaker,
        inlineNotification = null,
        itemHangerMessages = [],
        plugins = {},
        onMounted = () => {},
        onToolbarAction = () => {}
    } = $props();

    // svelte-ignore state_referenced_locally
    const status = getTestSessionStatusStore(serviceCallId);
    // svelte-ignore state_referenced_locally
    const testStateStore = getTestStateStore(serviceCallId);
    // svelte-ignore state_referenced_locally
    const toolsStore = getTestSessionUserDataService(serviceCallId).getToolsStore();
    // svelte-ignore state_referenced_locally
    const navigationFeedbacksStore = getNavigationFeedbacksStore(serviceCallId);

    //list of main test runner areas
    const areas = $state({});

    // For header & footer scroll shadows:
    let fullyScrolledUp = $state(true);
    let fullyScrolledDown = $state(true);

    const panelId = generateElementId('panel');
    const a11yPanelId = generateElementId('panel');

    let menuPanelOpen = $state(false);

    const showUserMenu = $derived(theme?.showUserMenu);
    const showMenuButton = $derived(!theme?.hideMenuButton);

    // startActions configures the buttons at the start of the HeaderBar
    let startActions = $derived(
        showMenuButton
            ? [
                  {
                      key: 'menu',
                      icon: 'menu',
                      label: __('Main menu'),
                      ariaLabel: __('Main menu'),
                      dataTestId: 'mainmenu',
                      ariaHasPopup: true,
                      ariaControls: panelId,
                      ariaExpanded: menuPanelOpen
                  }
              ]
            : []
    );

    // plugins toolbar init + store reactivity
    // svelte-ignore state_referenced_locally
    const toolbarItemsApi = createToolbarItemsApi(plugins, toolsStore);
    // endActions configures the buttons at the end of the HeaderBar
    let endActions = $derived(toolbarItemsApi.getToolbarActions($toolsStore));

    /**
     * Header Bar items click handler
     * @param {Event} event
     */
    function handleHeaderBarAction(event) {
        // forward all HeaderBar events to qti.js which will broadcast
        onToolbarAction(event);

        if (event.key === 'menu') {
            menuPanelOpen = true;
        }
    }

    /**
     * Returns if area should be hidden from testLayout based on areaHider plugin config
     * @param {string} areaId
     * @returns {boolean}
     */
    function isHiddenArea(areaId) {
        return plugins.areaHider && plugins.areaHider.hiddenAreas && plugins.areaHider.hiddenAreas.includes(areaId);
    }

    // floating tools
    let floatingToolsHeight = $state(0); // measured from element

    let windowWidth = $state();
    let windowHeight = $state();
    $effect(() => {
        setScreenSize(windowWidth);
    });

    let headerBarHidden = $derived($status === testSessionStatus.overlay && $screenSize.mobile);

    // innerHeight from svelte always return value in px. It creates situation where real test runner height differs from innerHeight after zooming/resizing. To avoid scrollbars, test runner height decreased on 1px.
    let safeWindowHeight = $derived(windowHeight && windowHeight - 1);

    let modalFeedbackIsShown = $derived($navigationFeedbacksStore && navigationFeedbacksStore.isAnyShown());

    $effect.pre(() => {
        if (modalFeedbackIsShown) {
            menuPanelOpen = false;
        }
    });

    const defaultLogo = {
        src: 'logo.svg',
        alt: __('TAO Logo')
    };
    let logo = $derived(theme?.logo || defaultLogo);

    let isItemModalFeedback = $derived($testStateStore && isItemModalFeedbackState(testStateStore.getTestContext()));

    let isMainAreaMain = $derived(
        [testSessionStatus.initial, testSessionStatus.loading, testSessionStatus.interacting].includes($status)
    );

    onMount(() => {
        setTimeout(() => {
            if (typeof onMounted === 'function') {
                onMounted({ areas });
            }
        }, 0);
    });

    onDestroy(() => {
        clearNotifications();
        clearTestLayoutStore();
    });
</script>

<style>
    /* local vars */
    :root {
        --testrunner-header-height: 7rem;
        --testrunner-footer-height: 9rem;
        --testrunner-item-top-padding: 0rem;
        --testrunner-item-bottom-padding: 0rem;
        --testrunner-item-max-width: 170.75rem;
        --testrunner-item-vertical-writing-min-margin: 9rem; /* .writing-mode-vertical-rl item: 1.5rem for scroll-shadow, 8rem for bookmark icon */
    }
    .use-a11y-text-styles {
        font-size: var(--fontsize-body);
        line-height: var(--line-height-default);
        letter-spacing: var(--letter-spacing, 0);
        word-spacing: var(--word-spacing, 0);
    }
    .not-displayed {
        display: none;
    }
    .test-runner {
        &.with-item-hanger {
            --testrunner-item-top-padding: 2rem;
            &.with-floating-tools {
                --testrunner-item-bottom-padding: 2rem;
            }
        }
        &.with-floating-tools {
            --testrunner-item-top-padding: var(--floating-tools-height, 5.5rem);
        }

        @media screen and (--mq-maxwidth-medium) {
            --testrunner-header-height: 6rem;
            --testrunner-footer-height: 9rem;
        }
        /* this variable will be passed to itemRunner */
        --testrunner-item-container-height: calc(
            var(--window-height) - var(--testrunner-header-height) - var(--testrunner-footer-height) -
                var(--testrunner-item-top-padding) - var(--testrunner-item-bottom-padding)
        );
        --testrunner-item-container-width: min(var(--testrunner-item-max-width), 100vw);
        --testrunner-item-container-offset-right: calc(
            (100vw - var(--testrunner-item-container-width)) / 2 - 0.125rem
        ); /* centered horizontally */

        --testrunner-aside-top-padding: calc(2rem + var(--testrunner-item-top-padding));
        --testrunner-aside-bottom-padding: calc(2rem + var(--testrunner-item-bottom-padding));
    }
    .test-runner {
        height: 100%;
        position: relative;
        overflow: hidden;
        display: flex;
        flex-direction: column;
        overscroll-behavior: none;
        background: var(--color-bg-default);
        color: var(--color-text-default);
        container-type: inline-size; /* for @container queries of children */
    }

    .top-bar {
        z-index: var(--layer-4);
        position: relative;
    }
    .bottom-bar {
        z-index: var(--layer-4);
    }
    .top-bar.overlay {
        z-index: calc(var(--layer-4) + 1);
    }
    .top-bar {
        & :global(img) {
            max-height: calc(var(--testrunner-header-height) - 2rem);
        }
        & :global(.headerbar .logo) {
            max-width: 25rem;
        }
        & :global(aside) {
            display: flex;
        }
    }
    .item-modalfeedback-nav {
        z-index: var(--layer-4);
        height: var(--testrunner-footer-height);
    }

    main {
        height: 100%;
        position: relative;
        overflow: hidden auto; /* independent scroll without asides */
        background: var(--color-bg-default);

        &.collapsed {
            height: 0;
        }

        & .test-content-columns {
            /* 1 or more columns */
            display: flex;
            flex-direction: row;
            /* Keep main-area background layers behind item and aside content. */
            position: relative;
            z-index: calc(var(--layer-0) + 1);

            & > * {
                flex: 1 1 0; /* equal columns */
            }

            & .qti-item-container {
                min-height: 100%;
                padding-top: var(--testrunner-item-top-padding);
                padding-bottom: var(--testrunner-item-bottom-padding);
                position: relative;

                @media screen and (--mq-minwidth-huge) {
                    max-width: var(--testrunner-item-max-width);
                    margin: 0 auto;
                }
                &:empty {
                    padding: 0;
                }

                & .item-content-container {
                    min-height: 100%;
                    position: relative;
                }
            }
        }

        /* 2 or more columns, side-by-side */
        &.with-aside-start,
        &.with-aside-end {
            & .test-content-columns {
                height: 100%;

                & .qti-item-container {
                    @container (1201px <= width) {
                        overflow: hidden auto; /* independent scroll */
                        max-width: min(var(--testrunner-item-max-width), 50cqw);
                    }
                }

                & #test-content-aside-start,
                & #test-content-aside-end {
                    min-height: 100%;
                    height: var(--testrunner-item-container-height, 100%);
                    padding: var(--testrunner-aside-top-padding) 4rem var(--testrunner-aside-bottom-padding);

                    @container (width < 1536px) {
                        padding-inline: 2rem;
                    }
                    @container (1201px <= width) {
                        overflow: hidden auto; /* independent scroll */
                        max-width: min(var(--testrunner-item-max-width), 50cqw);
                    }
                }
            }
        }

        & .scroll-last-child {
            height: 1px;
        }
    }

    @container (width < 1201px) {
        /* stacked columns */
        main {
            & .test-content-columns {
                display: block;
                height: auto;

                & .qti-item-container {
                    overflow: unset;
                }
            }

            &.with-aside-start,
            &.with-aside-end {
                height: auto;

                & .test-content-columns {
                    height: auto;

                    /* no independent scroll when stacked columns */
                    & .qti-item-container {
                        --testrunner-item-bottom-padding: 4rem;
                        padding-bottom: var(--testrunner-item-bottom-padding);
                    }
                    & #test-content-aside-start,
                    & #test-content-aside-end {
                        min-height: 100%;
                        height: var(--testrunner-item-container-height, 100%);
                        overflow: unset;
                        padding-inline: 4rem;

                        @container (width < 768px) {
                            padding-inline: 2rem;
                        }
                    }
                }
            }
        }
    }

    .panel-content {
        height: 100%;
    }

    .header-bar-content {
        width: 100%;
        display: flex;

        & :global(h1) {
            order: 1;
            overflow: hidden; /* so it can shrink horizontally and get ellipsis */
        }
        & :global(h1 ~ .livesave) {
            order: 2;
            padding-inline-start: 2rem;
        }
    }

    @media screen and (--mq-maxwidth-medium) {
        /* reduce space taken by LiveSaveIndicator */
        .header-bar-content {
            & :global(h1 ~ .livesave) {
                min-width: 4rem;
                & :global(.status) {
                    display: none;
                }
            }
        }
    }
    :global(body[data-zoom-level='150']),
    :global(body[data-zoom-level='175']),
    :global(body[data-zoom-level='200']) {
        /* reduce space taken by LiveSaveIndicator */
        & .header-bar-content {
            & :global(h1 ~ .livesave) {
                min-width: 4rem;
                & :global(.status) {
                    display: none;
                }
            }
        }
    }

    .hidden-item-container {
        position: absolute;
        z-index: var(--layer-back);
        left: 0;
        right: 0;
        top: 0;
        bottom: 0;
        overflow: visible;
    }

    .transition-wrapper {
        display: contents;
    }

    .floating-toolbars-wrapper {
        pointer-events: none; /* it may cover interactive elements */
        position: absolute;
        inset-inline-end: 0;
        top: 100%;
        margin: 0 4rem; /* should not cover item scrollbar */
        @media screen and (--mq-maxwidth-small) {
            margin: 0 1rem;
        }
    }

    /* override NotificationContainer's default positioning */
    .notification-container-wrapper {
        pointer-events: none; /* it may cover interactive elements */
        position: absolute;
        width: 100%;
        inset-inline-end: 0;
        top: calc(var(--testrunner-header-height) + var(--testrunner-item-top-padding));
        margin: 0 3rem; /* should not cover item scrollbar */
        z-index: calc(var(--layer-5) - 1); /* behind timeout/security dialog */
        @media screen and (--mq-maxwidth-small) {
            margin: 0;
        }
        & :global(.notification-wrapper) {
            pointer-events: initial;
        }
    }

    .inline-notification {
        margin: 0 4rem 1.25rem;
    }

    :global(.modal-wrapper.modal-wrapper) {
        z-index: var(--layer-5);
    }

    /* see 'bookletExport' plugin */
    :global(body.booklet-export-mode) {
        & .qti-item-container {
            width: var(--testrunner-item-max-width);
        }
        & main {
            overflow-x: auto;
        }
    }

    /* see item-runner */
    :global(body.item-writing-mode-vertical-rl) {
        --testrunner-item-max-width: calc(100vw - 2 * var(--testrunner-item-vertical-writing-min-margin));
        & .qti-item-container {
            margin: 0 var(--testrunner-item-vertical-writing-min-margin);
        }
    }

    @media print {
        .test-runner,
        .test-content,
        main,
        .test-content-columns,
        .qti-item-container {
            height: auto !important;
            overflow: visible !important;
        }
        .test-runner,
        .test-content-columns {
            display: block !important; /* seems better for Firefox in some cases */
        }
        .feedback-dialogs-wrapper, /* Safari, x-tao-printable, Ctrl+P, pauseOnBlur+forceFullscreen: security modal will cut item content in the middle */
        .test-runner :global(.draggable-modal),
        .test-runner :global(.not-printable) {
            display: none !important;
        }
    }
</style>

<!-- TODO: this should be the primary place in the app where screen dimensions are measured, and set to the store.
Other instances of bind:inner* could be replaced by the screenSizeStore, if it is available. -->
<svelte:window bind:innerWidth={windowWidth} bind:innerHeight={windowHeight} />

<div
    class="test-runner use-a11y-text-styles {$status || testSessionStatus.initial}"
    class:with-item-hanger={itemHangerMessages.length}
    class:with-floating-tools={floatingToolsHeight > 0}
    style="
        --window-height:{safeWindowHeight ? `${safeWindowHeight}px` : '100vh'};
        --floating-tools-height:{floatingToolsHeight}px;
    "
    aria-hidden={modalFeedbackIsShown ? true : void 0}
    bind:this={areas.testRunner}
    class:not-displayed={isHiddenArea('testRunner')}>
    <div bind:this={areas.jumpMenu} class:not-displayed={isHiddenArea('jumpMenu')}></div>
    <div
        id="test-top-bar"
        class="top-bar not-printable"
        class:overlay={$status === testSessionStatus.overlay}
        class:shadow-bottom={!fullyScrolledUp}
        class:hidden={$status === testSessionStatus.proctorwait}
        bind:this={areas.topBar}
        class:not-displayed={isHiddenArea('topBar')}>
        <div class:hidden={headerBarHidden}>
            <HeaderBar
                {startActions}
                {endActions}
                maxEndActions={$screenSize.mobile ? 4 : 9}
                logoSrc={logo.src}
                logoAltText={logo.alt}
                onAction={handleHeaderBarAction}>
                <div class="header-bar-content" bind:this={areas.header} class:not-displayed={isHiddenArea('header')}>
                </div>
                {#snippet asideAfter()}
                    {#if showUserMenu}
                        <UserMenu {testTaker} />
                    {/if}
                {/snippet}
            </HeaderBar>
        </div>
        <div
            class="floating-toolbars-wrapper"
            bind:this={areas.tools}
            class:not-displayed={isHiddenArea('tools')}
            use:resizeObserve
            onresized={e => (floatingToolsHeight = e.detail.height)}>
            <FloatingToolbarAreas {serviceCallId} {plugins} />
        </div>
    </div>

    <!-- This should be the only NotificationContainer rendered in the app.
    Its API methods allow any code to add or remove Notifications here. -->
    <div class="notification-container-wrapper not-printable">
        <NotificationContainer />
    </div>

    <!-- svelte-ignore a11y_no_noninteractive_tabindex -->
    <main
        id="test-main"
        aria-labelledby="a11y-main"
        role={isMainAreaMain ? 'main' : 'none'}
        tabindex={isMainAreaMain ? '0' : '-1'}
        aria-hidden={isMainAreaMain ? void 0 : 'true'}
        bind:this={areas.main}
        class:with-aside-start={$testLayoutStore.asideStart}
        class:with-aside-end={$testLayoutStore.asideEnd}
        class:collapsed={$status === testSessionStatus.loading}
        class:not-displayed={isHiddenArea('main')}>
        <div
            class="scroll-first-child"
            {@attach createVisibilityObserver(areas.main)}
            onisVisible={e => (fullyScrolledUp = e.detail)}>
        </div>

        <!-- svelte-ignore a11y_missing_content -->
        <h2 id="a11y-main" class="visually-hidden" tabindex="-1"></h2>
        <!-- tabindex because it should be programmatically focusable -->

        <div class="test-content-columns">
            <aside
                id="test-content-aside-start"
                class="not-printable"
                class:not-displayed={!$testLayoutStore.asideStart || $status !== testSessionStatus.interacting}
                role={$status === testSessionStatus.overlay ? 'none' : 'aside'}
                aria-hidden={$status === testSessionStatus.overlay ? 'true' : void 0}
                bind:this={areas.asideStart}>
            </aside>

            <div
                class="qti-item-container"
                class:not-displayed={isHiddenArea('content')}
                class:hidden-item-container={$status === testSessionStatus.loading ||
                    $status === testSessionStatus.proctorwait}
                aria-hidden={$status === testSessionStatus.loading ? 'true' : void 0}>
                {#if inlineNotification}
                    <div class="inline-notification">
                        <Feedback status="info" fullwidth content={inlineNotification.message} />
                    </div>
                {/if}
                <div class="item-content-container" bind:this={areas.content}></div>
            </div>

            <aside
                id="test-content-aside-end"
                class="not-printable"
                class:not-displayed={!$testLayoutStore.asideEnd || $status !== testSessionStatus.interacting}
                role={$status === testSessionStatus.overlay ? 'none' : 'aside'}
                aria-hidden={$status === testSessionStatus.overlay ? 'true' : void 0}
                bind:this={areas.asideEnd}>
            </aside>
        </div>

        <div
            class="scroll-last-child"
            {@attach createVisibilityObserver(areas.main)}
            onisVisible={e => (fullyScrolledDown = e.detail)}>
        </div>
        <!-- div.proctorwait and some floating tools also get injected here -->
    </main>

    <div class="transition-wrapper">
        {#if $status === testSessionStatus.loading}
            <Transition text={__('loading')} subtext={__('Please wait a little longer while your content loads')} />
        {/if}
    </div>

    <nav
        id="test-navigation"
        class="bottom-bar do-not-read not-printable"
        class:shadow-top={!fullyScrolledDown}
        class:hidden={$status === testSessionStatus.proctorwait || isItemModalFeedback}
        class:not-displayed={isHiddenArea('navigation')}
        aria-hidden="true"
        bind:this={areas.navigation}>
        <h2 class="visually-hidden">{__('Test Navigation')}</h2>
    </nav>

    <nav
        class="item-modalfeedback-nav"
        class:hidden={!isItemModalFeedback ||
            $status === testSessionStatus.proctorwait ||
            $status === testSessionStatus.overlay}
        class:shadow-top={!fullyScrolledDown}
        style="--nav-height: var(--testrunner-footer-height)"
        bind:this={areas.itemModalFeedbackNavigator}>
    </nav>

    <div class="item-hanger-wrapper not-printable">
        {#if itemHangerMessages && itemHangerMessages.length}
            <ItemHanger messages={itemHangerMessages} bottom={floatingToolsHeight > 0} />
        {/if}
    </div>

    <Overlay open={$status === testSessionStatus.overlay}>
        {#snippet header()}
            <div bind:this={areas.overlayHeader} class:not-displayed={isHiddenArea('overlayHeader')}></div>
        {/snippet}
        {#snippet content()}
            <div bind:this={areas.overlayContent} class:not-displayed={isHiddenArea('overlayContent')}></div>
        {/snippet}
        {#snippet footer()}
            <div bind:this={areas.overlayFooter} class:not-displayed={isHiddenArea('overlayFooter')}></div>
        {/snippet}
    </Overlay>
</div>

<!-- the following areas are outside areas.testRunner -->

<Panel bind:open={menuPanelOpen} id={panelId} side="start">
    <div class="panel-content use-a11y-text-styles" bind:this={areas.panel} class:not-displayed={isHiddenArea('panel')}>
    </div>
</Panel>

<div class="panel-wrapper use-a11y-text-styles" bind:this={areas.a11yMenuPanel} id={a11yPanelId}></div>

<div class="feedback-dialogs-wrapper use-a11y-text-styles" bind:this={areas.feedbackDialogs}>
    <FeedbackDialogsContainer {serviceCallId} />
</div>
